/**
 * IST (Asia/Kolkata) timezone helpers.
 * The whole app shows and computes "today" / "now" in IST regardless of the
 * user's browser timezone. Storage timestamps remain UTC (timestamptz);
 * formatting them goes through `formatIst` for display.
 */
import { format as fnsFormat } from "date-fns";
import { formatInTimeZone, toZonedTime } from "date-fns-tz";

export const IST_TZ = "Asia/Kolkata";

/** Current Date object shifted into IST wall-clock (use for date-fns calc). */
export function nowIst(): Date {
  return toZonedTime(new Date(), IST_TZ);
}

/** Today's date in IST as "yyyy-MM-dd". */
export function todayIstIso(): string {
  return formatInTimeZone(new Date(), IST_TZ, "yyyy-MM-dd");
}

/** Return a copy with a locked date field refreshed to the current IST date. */
export function withTodayIstDate<T extends Record<string, unknown>, K extends keyof T>(
  values: T,
  field: K,
): T {
  return { ...values, [field]: todayIstIso() };
}

/** Current IST time as "HH:mm". */
export function nowIstHHmm(): string {
  return formatInTimeZone(new Date(), IST_TZ, "HH:mm");
}

/** Current IST timestamp as ISO string (kept in UTC but representing now). */
export function nowIsoUtc(): string {
  return new Date().toISOString();
}

/** Format a Date / ISO string / timestamptz value in IST with a date-fns pattern. */
export function formatIst(
  value: Date | string | number | null | undefined,
  pattern: string,
): string {
  if (value === null || value === undefined || value === "") return "";
  try {
    return formatInTimeZone(value as Date | string | number, IST_TZ, pattern);
  } catch {
    return "";
  }
}

/** Convert any input to an IST-zoned Date for date-fns operations. */
export function toIst(value: Date | string | number): Date {
  return toZonedTime(value, IST_TZ);
}

/** Format a date-only "yyyy-MM-dd" string with a date-fns pattern (no TZ shift). */
export function formatDateOnly(value: string | null | undefined, pattern: string): string {
  if (!value) return "";
  // Treat as IST midnight to avoid off-by-one in negative offsets.
  return formatInTimeZone(`${value}T00:00:00+05:30`, IST_TZ, pattern);
}

// Re-export raw date-fns format for convenience (so callers can swap easily).
export { fnsFormat };
